/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { setUser } from 'actions/userActions';
import { useSignUpMutation, useUpdateUserMutation } from 'services/user';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'hooks/useSearchParams';
import toast from 'react-hot-toast';
import strings from '../../../strings';
import { userSelector } from '../../../redux/selectors';
import { legalDocumentsVersion } from '../../../settings/legalDocumentsVersion';
import { useClaim } from './useClaim';

export const useClaimProfile = ({ company, activeNetwork, isOnboarding }) => {
  const user = useSelector(userSelector);
  const [signUp, { isLoading }] = useSignUpMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const formRef = useRef(null);
  const [{ status }, setStatus] = useSearchParams({ status: 'sign_up' });

  const { claimOrganization, isClaiming } = useClaim();

  useEffect(() => {
    if (status !== 'email_mismatch' || !user?.id) {
      setStatus({ status: 'sign_up' });
    }
  }, []);

  const initialValues = {
    avatarUrl: user.avatarUrl || '',
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    termsAccepted: false,
    password: '',
  };
  const dispatch = useDispatch();

  const formSchema = Yup.object().shape({
    firstName: Yup.string().label('First name').required('First name is required'),
    lastName: Yup.string().label('Last name').required('Last name is required'),
    email: isOnboarding
      ? Yup.string()
          .email('Please enter a valid email address')
          .test('matchInvitedEmail', 'Email must match your invited email', (value) => value === user.email)
          .required('Email is required')
      : Yup.string()
          .email('Please enter a valid email address')
          .matches(
            new RegExp(`^[A-Z0-9._%+-]+@${company?.domain?.replace(/\./g, '\\.')}$`, 'i'),
            `You must use an email with a domain associated with this company account. If you want to use a different one, contact us.`,
          )
          .required('Email is required'),
    password: Yup.string().min(8, strings.validation.passwordLength).required('Password is required'),
    termsAccepted: Yup.boolean().oneOf([true], strings.validation.termsAccepted),
  });

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      collectionId: activeNetwork.id,
      privacyPolicyVersion: legalDocumentsVersion.privacyPolicyVersion,
      termsPolicyVersion: legalDocumentsVersion.termsPolicyVersion,
      claimOrgProfile: company?.id,
      onboardingCompleted: true,
      confirmed: true,
      role: 'company_admin',
    };

    const { error, data: userData } = await (user?.email && user.email === data?.email?.trim()
      ? updateUser({ ...payload, email: user.email })
      : signUp({ ...payload, email: data.email.trim() }));

    if (!error && userData) {
      dispatch(setUser(userData));
    }

    if (userData?.confirmed) {
      await claimOrganization({ activeNetwork, company, userData });

      return;
    }

    if (!error && !userData?.confirmed) {
      setStatus({ status: 'verify_email' });
      return;
    }

    const errors = error?.data?.errors;
    const emailError = errors?.find(({ title }) => title?.includes('Email has already been taken'));

    if (emailError) {
      formRef.current.setErrors({
        email: 'You can’t use this address because there is another account using it.',
      });
    }

    const defaultError = errors?.[0]?.title;
    if (defaultError && !emailError) {
      toast(defaultError, {
        icon: 'error',
        id: 'claim-profile-error',
      });
    }

    if (error && !errors?.length && !emailError) {
      toast(error?.data?.error || 'Something went wrong', {
        icon: 'error',
        id: 'claim-profile-error',
      });
    }
  };

  return {
    initialValues,
    formSchema,
    onSubmit,
    isLoading: isUpdating || isLoading || isClaiming,
    status,
    formRef,
    updateUser,
    user,
  };
};
